/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useState, useEffect, useMemo } from 'react';
import './css/Table.css';
import Button from '../components/Button';
import Modal from './Modal';
import Icon from './ui/Icon/Icon';
import { IQuestion, IQuestionnaire } from '../types/questionnaire';
import { useNavigate } from 'react-router-dom';
import { JobsService, UserData } from '../api/JobsService';
import { getCompanyIdFromToken } from '../utils/tokenHelper';
import Toast from './Toast';
import { Tooltip } from '@jobtarget/ui-library'
import { DEFAULT_QUESTIONNAIRE_TOOLTIP } from '../data/constants/constants'
import { SetDefaultModal, SwitchDefaultModal, RemoveDefaultModal } from './questionnaires/default-questionnaire-modals';
import { ReactivateQuestionnaireModal } from './questionnaires/reactivate-questionnaire-modals';
import { FormBuilderService } from '../api/FormBuilderService';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes } from '@fortawesome/free-solid-svg-icons';
import '../components/jobs/QuestionnaireAttachmentDrawer.css';

interface TableProps {
  data: IQuestionnaire[];
  // VI-579: value `undefined` means the count is still being fetched lazily on
  // Questionnaires tab mount; render '-' for that cell so we never show a misleading 0.
  // Number (including 0) = loaded count.
  activeJobsCounts?: Record<string, number | undefined>;
  // VI-612: total completed screener submissions per questionnaire for the "Completed" column.
  // `undefined` = still loading (render '-'); number (including 0) = loaded count.
  completedCounts?: Record<string, number | undefined>;
  // VI-579 follow-up (Bug 3): when the Preview / Reactivate modal's AttachedJobsList finishes
  // its fresh fetch, we call this ref's current function with { absolute: count } so the
  // cached count in QuestionnairesSection (and sessionStorage) snaps to the true number.
  activeJobsCountUpdaterRef?: React.MutableRefObject<
    (questionnaireId: string, change: { delta?: number; absolute?: number }) => void
  >;
  onAddToJob?: (jobNumber: number, itemId: string) => void;
  onPreview?: (itemId: string) => void;
  onStatusChange?: (itemId: string, newStatus: boolean) => void;
  onSetDefault?: (questionnaireId: string) => Promise<void> | void;
  onRemoveDefault?: (questionnaireId: string) => Promise<void> | void;
  users: Record<number, UserData>;
  loadingUsers: boolean;
}

const Table: React.FC<TableProps> = ({ 
  data,
  activeJobsCounts = {},
  completedCounts = {},
  activeJobsCountUpdaterRef,
  onAddToJob, 
  onPreview, 
  onStatusChange,
  onSetDefault,
  onRemoveDefault,
  users = {},
  loadingUsers = false
}) => {
  const navigate = useNavigate();
  const [defaultLoadingId, setDefaultLoadingId] = useState<string | null>(null);
  const [defaultAction, setDefaultAction] = useState<'setting' | 'removing' | null>(null);
  const [highlightId, setHighlightId] = useState<string | null>(null);

  // VI-725 Step E: preview modal is React-controlled now (was a Bootstrap data-toggle modal).
  const [previewId, setPreviewId] = useState<string | null>(null);

  // VI-725 Step E: row kebab menu is React-controlled now (was a Bootstrap data-toggle dropdown).
  const [openKebabId, setOpenKebabId] = useState<string | null>(null);

  useEffect(() => {
    if (!openKebabId) return;
    const onDocClick = (e: MouseEvent) => {
      if (!(e.target as HTMLElement).closest('.questionnaire-kebab')) setOpenKebabId(null);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpenKebabId(null);
    };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [openKebabId]);

  // Modal state
  const [modalState, setModalState] = useState<{
    type: 'set' | 'switch' | 'remove' | 'reactivate' | null;
    questionnaireId: string;
    questionnaireName: string;
  }>({ type: null, questionnaireId: '', questionnaireName: '' });

  const [isReactivating, setIsReactivating] = useState(false);

  // Toast alert state
  const [defaultAlert, setDefaultAlert] = useState<{
    isOpen: boolean;
    message: string;
    type: 'set' | 'remove' | 'reactivate';
  }>({ isOpen: false, message: '', type: 'set' });

  useEffect(() => {
    if (defaultAlert.isOpen) {
      const timer = setTimeout(() => {
        setDefaultAlert(prev => ({ ...prev, isOpen: false }));
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [defaultAlert.isOpen]);

  // Sort data so the default questionnaire always appears first
  const sortedData = useMemo(() => {
    return [...data].sort((a, b) => {
      if (a.isDefault && !b.isDefault) return -1;
      if (!a.isDefault && b.isDefault) return 1;
      return 0;
    });
  }, [data]);

  const closeModal = () => {
    setModalState({ type: null, questionnaireId: '', questionnaireName: '' });
  };

  const openSetDefaultModal = (item: IQuestionnaire) => {
    const hasExistingDefault = data.some(q => q.isDefault);
    setModalState({
      type: hasExistingDefault ? 'switch' : 'set',
      questionnaireId: item.questionnaireId,
      questionnaireName: item.questionnaireName,
    });
  };

  const openRemoveDefaultModal = (item: IQuestionnaire) => {
    setModalState({
      type: 'remove',
      questionnaireId: item.questionnaireId,
      questionnaireName: item.questionnaireName,
    });
  };

  const openReactivateModal = (item: IQuestionnaire) => {
    setModalState({
      type: 'reactivate',
      questionnaireId: item.questionnaireId,
      questionnaireName: item.questionnaireName,
    });
  };

  const handleReactivate = async () => {
    const { questionnaireId, questionnaireName } = modalState;
    if (!questionnaireId) return;

    setIsReactivating(true);
    try {
      await FormBuilderService.patchQuestionnaire(questionnaireId, { active: true });
      onStatusChange?.(questionnaireId, true);
      closeModal();
      setDefaultAlert({
        isOpen: true,
        message: `${questionnaireName} reactivated`,
        type: 'reactivate',
      });
    } catch (error: unknown) {
      console.error('Error reactivating questionnaire:', error);
    } finally {
      setIsReactivating(false);
    }
  };

  const handleSetDefault = async (questionnaireId: string) => {
    const questionnaireName = modalState.questionnaireName;
    closeModal();
    setDefaultLoadingId(questionnaireId);
    setDefaultAction('setting');
    try {
      await onSetDefault?.(questionnaireId);
      setHighlightId(questionnaireId);
      setTimeout(() => setHighlightId(null), 600);
      setDefaultAlert({
        isOpen: true,
        message: `${questionnaireName} has been set as default.`,
        type: 'set',
      });
    } finally {
      setDefaultLoadingId(null);
      setDefaultAction(null);
    }
  };

  const handleRemoveDefault = async (questionnaireId: string) => {
    const questionnaireName = modalState.questionnaireName;
    closeModal();
    setDefaultLoadingId(questionnaireId);
    setDefaultAction('removing');
    try {
      await onRemoveDefault?.(questionnaireId);
      setDefaultAlert({
        isOpen: true,
        message: `${questionnaireName} has been removed as default.`,
        type: 'remove',
      });
    } finally {
      setDefaultLoadingId(null);
      setDefaultAction(null);
    }
  };

  const getUserDisplayName = (userId: number | undefined) => {
    if (!userId) return 'Unknown';
    if (loadingUsers) {
      return <small className="text-muted">Loading...</small>;
    }
    
    const user = users[userId];
    if (!user) {
      return 'Unknown';
    }
    
    return `${user.contactInfo.firstName} ${user.contactInfo.lastName}`;
  };
  const [hasShownDeactivateToast, setHasShownDeactivateToast] = useState(false);

  // Show toast only on first deactivation
  const handleToastOnDeactivate = () => {
    if (!hasShownDeactivateToast) {
      setHasShownDeactivateToast(true);
    }
  };

  const handleEdit = (questionnaireId: string) => {
    navigate(`/v1/recruiter/questionnaire-builder/${questionnaireId}`);
  };

  const handleClone = (questionnaire: IQuestionnaire) => {
    navigate('/v1/recruiter/questionnaire-builder', {
      state: {
        isCloning: true,
        cloneData: {
          questionnaireName: `${questionnaire.questionnaireName} (Copy)`,
          contentType: questionnaire.contentType,
          questions: questionnaire.questions.map(q => ({
            text: q.text || q.questionText,
            type: q.type,
            required: q.required,
            options: q.options
          }))
        }
      }
    });
  };

  const formatDate = (date: string | Date) => {
    return new Intl.DateTimeFormat('en-US', {
      month: '2-digit',
      day: '2-digit',
      year: 'numeric'
    }).format(new Date(date));
  };

  return (
    <div className={`table-responsive card ${sortedData.length === 1 ? 'single-row' : ''}`} style={{ overflow: 'visible' }}>
      <table className="table mb-0">
        <thead>
          <tr className="bg-light text-align-left">
            <th>Name</th>
            <th>Created by</th>
            <th>Status</th>
            <th>Questions</th>
            <th>Jobs Attached</th>
            <th>Completed</th>
            <th style={{ textAlign: 'center' }}>Actions</th>
          </tr>
        </thead>
        <tbody>
          {sortedData.map((item, index) => (
            <tr key={item.questionnaireId} className={highlightId === item.questionnaireId ? 'row-highlight' : ''}>
              <td>
                <div className="d-flex flex-column text-align-left">
                  <div className="d-flex align-items-center" style={{ gap: '12px' }}>
                    <button
                      type="button"
                      className="btn btn-link p-0 questionnaire-name-link"
                      onClick={() => setPreviewId(item.questionnaireId)}
                      id={`questionnaire-name-link-${item.questionnaireId}`}
                    >
                      {item.questionnaireName}
                    </button>
                    {defaultLoadingId === item.questionnaireId && defaultAction === 'setting' && (
                      <span className="bg-light-warning px-2 py-1 rounded d-inline-block text-warning" style={{ fontSize: '0.7rem', whiteSpace: 'nowrap' }}>
                        <i className="fa fa-spinner fa-spin mr-1"></i>Setting Default...
                      </span>
                    )}
                    {defaultLoadingId === item.questionnaireId && defaultAction === 'removing' && (
                      <span className="bg-light-danger px-2 py-1 rounded d-inline-block text-danger" style={{ fontSize: '0.7rem', whiteSpace: 'nowrap' }}>
                        <i className="fa fa-spinner fa-spin mr-1"></i>Removing...
                      </span>
                    )}
                    {item.isDefault && defaultLoadingId !== item.questionnaireId && (
                      <Tooltip
                        message={(<>{DEFAULT_QUESTIONNAIRE_TOOLTIP}<br /><i>Default since {item.defaultedAt ? new Intl.DateTimeFormat('en-US', { month: 'long', day: 'numeric', year: 'numeric' }).format(new Date(item.defaultedAt)) : '—'}</i></>) as unknown as string}
                        position="top"
                        maxWidth={240}
                        textAlign="center"
                      >
                        <span className="bg-light-info px-2 py-1 rounded" 
                          style={{ fontSize: '0.7rem', fontWeight: 500, display: 'inline-flex', alignItems: 'center', gap: '4px', whiteSpace: 'nowrap', cursor: 'help', lineHeight: 1, color: '#5C3D9D' }}>
                          <span style={{ lineHeight: 1 }}>Default</span>
                          <i className="fal fa-circle-info" style={{ fontSize: '0.7rem', lineHeight: 1 }}></i>
                        </span>
                      </Tooltip>
                    )}
                  </div>
                    <div className='d-flex align-items-center mt-1'>
                    <span>
                      <img 
                        src={item.contentType === 'Video' ? '/svgs/video.svg' : item.contentType === 'Audio' ? '/svgs/mic.svg' : ''}
                        alt={item.contentType} 
                        width="16" 
                        height="16" 
                        className="text-secondary" 
                      />
                    </span>
                        <small className="text-muted ml-2"> {item.contentType} </small>
                    </div>
                </div>
              </td>
              <td>
                <div>
                  <div className="text-align-left">{getUserDisplayName(item.userId)}</div>
                  <div className="text-secondary small text-align-left">on {formatDate(item.createdAt)}</div>
                </div>
              </td>
              <td>
                <div className='d-flex align-items-center'>
                    <div className={`status-dot ${item.active === true ? 'active' : 'inactive'} me-2`}></div>
                    {item.active ? 'Active' : 'Inactive'}
                </div>
              </td>
              <td className='text-align-left'>{item.questions.length}</td>
              <td className='text-align-left'>
                {activeJobsCounts[item.questionnaireId] !== undefined
                  ? activeJobsCounts[item.questionnaireId]
                  : '-'}
              </td>
              {/* VI-612: total completed screener submissions for this questionnaire */}
              <td className='text-align-left'>
                {completedCounts[item.questionnaireId] !== undefined
                  ? completedCounts[item.questionnaireId]
                  : '-'}
              </td>
              <td style={{ textAlign: 'center' }}>
                <div className="d-flex align-items-center justify-content-center gap-2">
                    <div className="me-2">
                        {item.active ? (
                            <Button 
                                text={`Add to Job`} 
                                variant="outline-primary" 
                                onClick={() => onAddToJob?.(index + 1, item.questionnaireId)}
                                id={`add-to-job-button-${item.questionnaireId}`}
                                style={{ whiteSpace: 'nowrap' }}
                            />
                        ) : (
                            <Button 
                                text={`Reactivate`} 
                                variant="outline-primary" 
                                onClick={() => openReactivateModal(item)}
                                id={`reactivate-questionnaire-button-${item.questionnaireId}`}
                                style={{ whiteSpace: 'nowrap' }}
                            />
                        )}
                    </div>
                    <div className="ml-2">
                        <button
                          className='btn btn-outline-primary ml-2'
                          onClick={() => setPreviewId(item.questionnaireId)}
                          id={`preview-questionnaire-button-${item.questionnaireId}`}
                        >
                          View
                        </button>
                    </div>
                    
                    <div className="dropdown questionnaire-kebab ml-3">
                        <button
                            className="btn btn-link btn-sm text-secondary"
                            type="button"
                            aria-haspopup="true"
                            aria-expanded={openKebabId === item.questionnaireId}
                            onClick={() => setOpenKebabId(prev => prev === item.questionnaireId ? null : item.questionnaireId)}
                            style={{ borderRadius: '4px', transition: 'background-color 0.2s ease', padding: '6px 10px' }}
                            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#F2F3F7'}
                            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                        >
                            <i className="fas fa-ellipsis-vertical"></i>
                        </button>

                        <div
                          className={`dropdown-menu dropdown-menu-right ${openKebabId === item.questionnaireId ? 'show' : ''}`}
                          role='menu'
                          style={{ display: openKebabId === item.questionnaireId ? 'block' : 'none' }}
                          onClick={() => setOpenKebabId(null)}
                        >
                          {item.active && !item.isDefault && (
                            <a 
                              className={`dropdown-item ${defaultLoadingId ? 'disabled' : ''}`}
                              href="#"
                              id={`set-default-questionnaire-button-${item.questionnaireId}`}
                              onClick={(e) => {
                                e.preventDefault();
                                if (defaultLoadingId) return;
                                openSetDefaultModal(item);
                              }}
                              style={defaultLoadingId ? { pointerEvents: 'none', opacity: 0.6 } : {}}
                            >
                              {defaultLoadingId === item.questionnaireId ? (
                                <><i className="fa fa-spinner fa-spin mr-2"></i>Setting Default...</>
                              ) : (
                                <><i className="fal fa-check mr-2"></i>Set as Default</>
                              )}
                            </a>
                          )}
                          {item.isDefault && (
                            <a 
                              className={`dropdown-item ${defaultLoadingId ? 'disabled' : ''}`}
                              href="#"
                              id={`remove-default-questionnaire-button-${item.questionnaireId}`}
                              onClick={(e) => {
                                e.preventDefault();
                                if (defaultLoadingId) return;
                                openRemoveDefaultModal(item);
                              }}
                              style={defaultLoadingId ? { pointerEvents: 'none', opacity: 0.6 } : {}}
                            >
                              {defaultLoadingId === item.questionnaireId ? (
                                <><i className="fa fa-spinner fa-spin mr-2"></i>Removing...</>
                              ) : (
                                <><i className="fal fa-xmark mr-2"></i>Remove Default</>
                              )}
                            </a>
                          )}
                          <a 
                            className="dropdown-item" 
                            href="#"
                            id={`edit-questionnaire-button-${item.questionnaireId}`}
                            onClick={(e) => {
                              e.preventDefault();
                              handleEdit(item.questionnaireId);
                            }}
                          >
                            <i className="fal fa-pen mr-2"></i>Edit
                          </a>
                          <a 
                            className="dropdown-item" 
                            href="#"
                            id={`clone-questionnaire-button-${item.questionnaireId}`}
                            onClick={(e) => {
                              e.preventDefault();
                              handleClone(item);
                            }}
                          >
                            <i className="fal fa-copy mr-2"></i>Clone
                          </a>
                          
                          {item.active && (
                            <a 
                              className="dropdown-item text-danger"
                              href="#"
                              id={`deactivate-questionnaire-button-${item.questionnaireId}`}
                              onClick={(e) => {
                                e.preventDefault();
                                onStatusChange?.(item.questionnaireId, false);
                                handleToastOnDeactivate();
                              }}
                            >
                              <i className="fal fa-circle-minus mr-2"></i>
                              Deactivate
                            </a>
                          )}
                        </div>
                    </div>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {/* The toast is shown when hasShownDeactivateToast is true */}
      {hasShownDeactivateToast && (
        <Toast 
          isOpen={true}
          onClose={() => {}}
          message={`Inactive Questionnaires maintain their association with jobs, <strong>however no Screeners will be sent when candidates apply.</strong>`}
          type="warning"
          title="Questionnaire Deactivated"
          autoClose={true}
          duration={10000}
        />
      )}

      {/* Default Questionnaire Toast */}
      {defaultAlert.isOpen && (() => {
        const isSuccess = defaultAlert.type === 'set' || defaultAlert.type === 'reactivate';
        const accentColor = isSuccess ? '#09423F' : '#343A40';
        return (
          <div className="toast-notification-container">
            <div className={`toast-notification ${isSuccess ? 'toast-success' : 'toast-detach'}`}>
              <Icon
                icon={`fal ${isSuccess ? 'fa-circle-check' : 'fa-empty-set'}`}
                style={{
                  fontSize: '20px',
                  color: accentColor,
                }}
                ariaHidden
              />
              <p className="toast-message mb-0" style={{ fontWeight: 500 }}>{defaultAlert.message}</p>
              <Icon
                icon="fal fa-xmark"
                className="toast-close-button"
                onClick={() => setDefaultAlert(prev => ({ ...prev, isOpen: false }))}
                style={{ fontSize: '16px', color: accentColor, cursor: 'pointer' }}
                ariaLabel="Close"
              />
            </div>
          </div>
        );
      })()}

      {/* Default Questionnaire Modals */}
      <SetDefaultModal
        isOpen={modalState.type === 'set'}
        questionnaireName={modalState.questionnaireName}
        onConfirm={() => handleSetDefault(modalState.questionnaireId)}
        onCancel={closeModal}
      />
      <SwitchDefaultModal
        isOpen={modalState.type === 'switch'}
        newQuestionnaireName={modalState.questionnaireName}
        onConfirm={() => handleSetDefault(modalState.questionnaireId)}
        onCancel={closeModal}
      />
      <RemoveDefaultModal
        isOpen={modalState.type === 'remove'}
        questionnaireName={modalState.questionnaireName}
        onConfirm={() => handleRemoveDefault(modalState.questionnaireId)}
        onCancel={closeModal}
      />
      <ReactivateQuestionnaireModal
        isOpen={modalState.type === 'reactivate'}
        questionnaireId={modalState.questionnaireId}
        isReactivating={isReactivating}
        onDismiss={() => {
          if (!isReactivating) closeModal();
        }}
        onClone={() => {
          const questionnaire = data.find(q => q.questionnaireId === modalState.questionnaireId);
          if (questionnaire) {
            closeModal();
            handleClone(questionnaire);
          }
        }}
        onReactivate={handleReactivate}
        onAttachedJobsCountResolved={(count) =>
          activeJobsCountUpdaterRef?.current(modalState.questionnaireId, { absolute: count })
        }
      />

      {/* VI-725 Step E: single React-controlled preview modal (replaces the per-row Bootstrap modal) */}
      {(() => {
        const previewItem = previewId ? sortedData.find(q => q.questionnaireId === previewId) : null;
        if (!previewItem) return null;
        return (
          <Modal
            id={previewItem.questionnaireId}
            questionnaireId={previewItem.questionnaireId}
            questionnaireName={previewItem.questionnaireName}
            questions={previewItem.questions as IQuestion[]}
            contentType={previewItem.contentType}
            isOpen={true}
            onClose={() => setPreviewId(null)}
            onAttachedJobsCountResolved={(count) =>
              activeJobsCountUpdaterRef?.current(previewItem.questionnaireId, { absolute: count })
            }
          />
        );
      })()}
    </div>
  );
};

export default Table;
